/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/

/**
 * Cross-subdomain user-id cookie.
 *
 * The API client sends the current user id in the `New-Api-User` header, read
 * from localStorage('uid'). localStorage is origin-scoped, so after a login on
 * the marketing apex (e.g. aiproxys.net) the console subdomain
 * (api.aiproxys.net) starts with an empty localStorage → no `New-Api-User` →
 * getSelf() 401 → the user is wrongly asked to log in again, even though the
 * session cookie (Domain=<apex>) is valid across subdomains.
 *
 * This mirrors the uid into a cookie scoped to the registrable domain
 * (`.aiproxys.net`), so the console subdomain can read it and pair it with the
 * already-shared session cookie. The value is a non-secret numeric id; auth is
 * still enforced by the HttpOnly session cookie + backend, so a JS-readable
 * cookie here is safe.
 */

const UID_COOKIE = 'uid'
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30 // 30d, matches the session cookie lifetime

/**
 * Registrable domain (last two labels) of the current host, used as the cookie
 * Domain so it is shared across the apex and its api subdomain. Returns '' for
 * localhost / bare IPs so the cookie stays host-only there.
 *
 * ponytail: last-two-labels only — correct for .net/.co/.com deployments here;
 * would mis-scope on multi-level TLDs (foo.co.uk). Revisit if such a domain ships.
 */
function registrableDomain(): string {
  if (typeof window === 'undefined') return ''
  const host = window.location.hostname
  if (!host.includes('.') || /^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return ''
  return host.split('.').slice(-2).join('.')
}

export function setSharedUid(userId: number | string): void {
  if (typeof document === 'undefined') return
  const d = registrableDomain()
  const domain = d ? `; Domain=.${d}` : ''
  document.cookie = `${UID_COOKIE}=${encodeURIComponent(String(userId))}; Path=/${domain}; Max-Age=${MAX_AGE_SECONDS}; SameSite=None; Secure`
}

export function getSharedUid(): string | null {
  if (typeof document === 'undefined') return null
  const m = document.cookie.match(/(?:^|;\s*)uid=([^;]+)/)
  return m ? decodeURIComponent(m[1]) : null
}

export function clearSharedUid(): void {
  if (typeof document === 'undefined') return
  const d = registrableDomain()
  const domain = d ? `; Domain=.${d}` : ''
  document.cookie = `${UID_COOKIE}=; Path=/${domain}; Max-Age=0; SameSite=None; Secure`
}
